import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { toastr } from "react-redux-toastr";
import { Main } from "layouts";
import Loader from "components/Loader";
import { client_v2, getApiErrorMessage } from "utils/api";
import { sort } from "./const/data";

import { ReactComponent as WarehouseIcon } from "icons/warehouse.svg";

const getItemStyle = (isDragging, draggableStyle) => ({
  ...draggableStyle,
  display: "table-row",
  userSelect: "none",
  background: isDragging ? "#fff" : "#fff",
});

const getRowColor = ({ warehouse_id, identifier }) => {
  if (![1, 2].includes(warehouse_id) || ["B01", "ADU"].includes(identifier)) {
    return "rgba(253, 57, 122, 0.4)";
  }
  if (warehouse_id === 1) {
    return "rgba(103, 0, 162, 0.4)";
  }
  if (warehouse_id === 2) {
    return "rgba(93, 120, 255, 0.4)";
  }

  return "#fff";
};

const WarehouseAisles = () => {
  const [is_regenerating, setRegenerating] = useState(false);
  const {
    isLoading: is_loading,
    isFetching: is_fetching,
    data,
    refetch,
  } = useQuery(
    ["warehouse_aisles"],
    () =>
      client_v2.get(
        `/warehouse_aisles?sort[column]=position&sort[order]=asc&per_page=1000&context=ui_index_positions`
      ),
    {
      onError: (err) => toastr.error("Błąd", getApiErrorMessage(err)),
      refetchInterval: false,
      refetchIntervalInBackground: false,
    }
  );

  const updateWarehouseAislePosition = async ({ id, position }) => {
    try {
      await client_v2.put(`/warehouse_aisles/${id}`, { position });
      refetch();
    } catch (error) {
      toastr.error("Błąd", getApiErrorMessage(error));
    }
  };

  const regenerateWarehousePositionIdentifiers = async () => {
    try {
      setRegenerating(true);
      await client_v2.post(
        `/warehouse_positions/regenerate_position_identifiers`
      );
    } catch (error) {
      toastr.error("Błąd", getApiErrorMessage(error));
    } finally {
      setRegenerating(false);
    }
  };

  const onDragEnd = (result) => {
    if (!result.destination) {
      return;
    }
    updateWarehouseAislePosition({
      id: result.draggableId,
      position: result.destination.index,
    });
  };

  return (
    <Main
      page={{
        name: "Alejki magazynowe",
        icon: <WarehouseIcon />,
        breadcrumbs: [
          {
            name: "Zarządzanie magazynem",
            path: "/warehouses",
          },
          { name: "Alejki magazynowe" },
        ],
        buttons: (
          <button
            disabled={is_regenerating}
            className="btn btn-sm btn-info"
            onClick={regenerateWarehousePositionIdentifiers}
          >
            {is_regenerating
              ? "Proszę czekać..."
              : "Odśwież identyfikatory pozycji"}
          </button>
        ),
      }}
    >
      <div className="main-list">
        {is_loading ? (
          <div className="main-list__loader">
            <Loader />
          </div>
        ) : (
          <div className="main-list__table">
            <table style={{ opacity: is_fetching ? 0.7 : 1 }}>
              <thead className="table-sort">
                <tr>
                  {sort.map(
                    ({ label = "", name = "", sortable = true, style = {} }) =>
                      sortable ? (
                        <th style={style} key={name}>
                          <button
                            type="button"
                            onClick={() =>
                              changeSort({
                                column: name,
                                direction: checkDirectionToChange({
                                  is_current_column:
                                    active_sort.column === name,
                                  active_direction: active_sort.direction,
                                }),
                              })
                            }
                            className={classNames("sort-button", {
                              active: active_sort.column === name,
                              asc:
                                active_sort.column === name &&
                                active_sort.direction === "asc",
                            })}
                          >
                            <span>{label}</span>
                            <ArrowIcon />
                          </button>
                        </th>
                      ) : (
                        <th style={style} key={name}>
                          <span>{label}</span>
                        </th>
                      )
                  )}
                </tr>
              </thead>
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="droppable">
                  {(provided) => (
                    <tbody {...provided.droppableProps} ref={provided.innerRef}>
                      {data?.data?.map(
                        ({
                          id,
                          name,
                          identifier,
                          position,
                          warehouse_room,
                          warehouse,
                        }) => (
                          <Draggable
                            key={id}
                            draggableId={String(id)}
                            index={position}
                          >
                            {(provided, snapshot) => (
                              <tr
                                key={id}
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                style={{
                                  ...getItemStyle(
                                    snapshot.isDragging,
                                    provided.draggableProps.style
                                  ),
                                  backgroundColor: getRowColor({
                                    warehouse_id: warehouse.id,
                                    identifier,
                                  }),
                                }}
                              >
                                <td>{id}</td>
                                <td>
                                  <strong>{name}</strong>
                                </td>
                                <td>
                                  <strong>{identifier}</strong>
                                </td>
                                <td>
                                  <strong>{position}</strong>
                                </td>
                                <td>
                                  <strong>{warehouse?.name}</strong>
                                </td>
                                <td>
                                  <strong>{warehouse_room?.name}</strong>
                                </td>
                              </tr>
                            )}
                          </Draggable>
                        )
                      )}
                      {provided.placeholder}
                    </tbody>
                  )}
                </Droppable>
              </DragDropContext>
            </table>
          </div>
        )}
      </div>
    </Main>
  );
};

export default WarehouseAisles;
